import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "./ui/Button";
import SheetsConnectChoices, {
  type SheetsConnectChoice,
} from "./SheetsConnectChoices";
import { pathForExistingDatasource } from "../lib/after-auth";
import {
  createSampleSheet,
  isSampleSheetUnavailable,
  startSampleSheetAnalysis,
} from "../lib/sample-sheet";
import { useAuth } from "../lib/useAuth";
import { useDatasources } from "../lib/useDatasources";
import { refreshDatasources } from "../lib/refresh-datasources";
import { ownDatasources } from "../lib/demo-preference";

/**
 * MOSA-364 / MOSA-908. On an empty data-sources page this is the choice
 * screen. With sources connected it stays a short connect link. PostgreSQL
 * stays behind "More connections".
 */
const SheetsConnectLead: React.FC<{ onMoreConnections: () => void }> = ({
  onMoreConnections,
}) => {
  const { token, user, guest } = useAuth();
  const { datasources, isInitialLoad } = useDatasources();
  const navigate = useNavigate();
  const signedIn = Boolean(user) && !guest;
  const hasOwnSources = ownDatasources(datasources).length > 0;

  const [sampleAvailable, setSampleAvailable] = useState(true);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState<string | null>(null);

  const startSample = async () => {
    setSampleError(null);
    setSampleLoading(true);
    try {
      const created = await createSampleSheet(token);
      if (signedIn) {
        await startSampleSheetAnalysis(token, created);
      }
      await refreshDatasources();
      navigate(pathForExistingDatasource(created));
    } catch (error) {
      if (isSampleSheetUnavailable(error)) {
        setSampleAvailable(false);
        setSampleError("The sample sheet is not available right now.");
        return;
      }
      setSampleError("Could not open the sample sheet. Try again.");
    } finally {
      setSampleLoading(false);
    }
  };

  const onChoose = (choice: SheetsConnectChoice) => {
    if (choice === "paste") {
      navigate("/connect/sheets?entry=paste");
      return;
    }
    if (choice === "browse") {
      navigate("/connect/sheets?entry=browse");
      return;
    }
    void startSample();
  };

  // While the first list is unknown, keep the page quiet.
  if (isInitialLoad) {
    return null;
  }

  if (!hasOwnSources) {
    return (
      <div className="space-y-2">
        <SheetsConnectChoices
          onChoose={onChoose}
          onMoreConnections={onMoreConnections}
          sampleAvailable={sampleAvailable}
          sampleLoading={sampleLoading}
          title="Start with a Google Sheet"
          detail="Paste a link. Choose a file from Drive. Or try a sample sheet."
          testId="sheets-connect-lead"
        />
        {sampleError && (
          <p
            className="text-sm text-od-danger"
            role="status"
            data-testid="sample-sheet-error"
          >
            {sampleError}
          </p>
        )}
      </div>
    );
  }

  return (
    <section
      className="rounded-lg border border-od-border bg-od-surface p-4 md:p-6"
      data-testid="sheets-connect-lead"
    >
      <h1 className="text-lg font-semibold text-od-text">
        Start with a Google Sheet
      </h1>
      <p className="mt-1 text-sm text-od-muted">
        Paste a link or choose a file from Drive. OnlyDash builds the panel.
      </p>
      <div className="mt-4 flex flex-col items-start gap-2">
        <Link
          to="/connect/sheets"
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md bg-od-primary px-3.5 text-xs font-semibold text-od-primary-contrast hover:bg-od-primary-hover active:bg-od-primary-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-surface sm:h-8 sm:min-h-0 sm:min-w-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
          data-testid="compare-sheets-link"
        >
          Connect a Google Sheet
        </Link>
        {!signedIn && (
          <span className="text-[13px] text-od-muted">
            You can sign up in the wizard. OnlyDash keeps your link.
          </span>
        )}
        <Button
          variant="link"
          size="sm"
          onClick={onMoreConnections}
          className="px-0 text-left text-[13px] underline decoration-od-border underline-offset-4 hover:decoration-od-primary"
          data-testid="more-connections"
        >
          More connections
        </Button>
      </div>
    </section>
  );
};

export default SheetsConnectLead;
